import React, { useState } from "react";
import HeaderNav from "@/components/HeaderNav";
import Footer from "@/components/Footer";
import { api, formatApiError, formatINR } from "@/lib/api";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Gift, IndianRupee, Search, CheckCircle2, Hourglass, XCircle, MessageCircle } from "lucide-react";
import { toast } from "sonner";
import { useSearchParams } from "react-router-dom";
import { useEffect } from "react";

const STATUS_STYLES = {
  pending: "bg-amber-500/15 text-amber-600 border-amber-500/30",
  paid: "bg-emerald-500/15 text-emerald-600 border-emerald-500/30",
  cancelled: "bg-destructive/15 text-destructive border-destructive/30",
};

export default function MyCredits() {
  const [params] = useSearchParams();
  const initialPhone = params.get("phone") || "";
  const [phone, setPhone] = useState(initialPhone);
  const [loading, setLoading] = useState(false);
  const [data, setData] = useState(null);

  const load = async (p) => {
    if (!p || p.length < 4) {
      toast.error("Enter your registered phone number");
      return;
    }
    setLoading(true);
    setData(null);
    try {
      const { data } = await api.get(`/referrals/mine?phone=${encodeURIComponent(p)}`);
      setData(data);
      if (data.credits.length === 0) {
        toast.info("No referral credits found for this number");
      }
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (initialPhone) load(initialPhone);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [initialPhone]);

  return (
    <div className="min-h-screen bg-background" data-testid="my-credits-page">
      <HeaderNav />
      <div className="relative overflow-hidden">
        <div className="absolute inset-0 tv-radial pointer-events-none" />
        <div className="absolute inset-0 tv-grid-bg opacity-40 pointer-events-none" />
        <div className="relative max-w-3xl mx-auto px-4 sm:px-6 py-12">
          <div className="text-center mb-8">
            <span className="tv-chip-emerald mb-4">
              <Gift className="w-3.5 h-3.5" /> Referral Cash
            </span>
            <h1 className="text-3xl sm:text-4xl lg:text-5xl font-bold tracking-tight">My Referral Credits</h1>
            <p className="text-muted-foreground mt-2">
              Enter the phone number you used to book. Every friend whose pickup you referred earns you ₹200.
            </p>
          </div>

          <Card className="mb-8">
            <CardContent className="p-6">
              <form
                onSubmit={(e) => { e.preventDefault(); load(phone.trim()); }}
                className="grid sm:grid-cols-[1fr_auto] gap-3 items-end"
                data-testid="credits-form"
              >
                <div className="grid gap-1.5">
                  <Label htmlFor="ph">Registered Phone Number</Label>
                  <Input
                    id="ph"
                    value={phone}
                    onChange={(e) => setPhone(e.target.value)}
                    placeholder="e.g. 9990001111"
                    className="font-mono"
                    data-testid="credits-phone-input"
                  />
                </div>
                <Button type="submit" disabled={loading} className="bg-primary hover:bg-primary/90 h-10" data-testid="credits-submit-btn">
                  <Search className="w-4 h-4 mr-1.5" />
                  {loading ? "Checking…" : "Check Credits"}
                </Button>
              </form>
            </CardContent>
          </Card>

          {data && (
            <>
              <div className="grid sm:grid-cols-2 gap-4 mb-6">
                <Card className="border-amber-500/40 bg-amber-500/5">
                  <CardContent className="p-5 flex items-center gap-4">
                    <div className="w-11 h-11 rounded-xl bg-amber-500/15 text-amber-600 flex items-center justify-center">
                      <Hourglass className="w-5 h-5" />
                    </div>
                    <div>
                      <div className="text-xs uppercase tracking-widest text-muted-foreground font-mono">Pending Payout</div>
                      <div className="mt-0.5 flex items-end gap-0.5 text-amber-600">
                        <IndianRupee className="w-5 h-5" />
                        <span className="text-2xl font-extrabold font-mono" data-testid="pending-total">{data.total_pending.toLocaleString("en-IN")}</span>
                      </div>
                    </div>
                  </CardContent>
                </Card>
                <Card className="border-emerald-500/40 bg-emerald-500/5">
                  <CardContent className="p-5 flex items-center gap-4">
                    <div className="w-11 h-11 rounded-xl bg-emerald-500/15 text-emerald-600 flex items-center justify-center">
                      <CheckCircle2 className="w-5 h-5" />
                    </div>
                    <div>
                      <div className="text-xs uppercase tracking-widest text-muted-foreground font-mono">Cash Received</div>
                      <div className="mt-0.5 flex items-end gap-0.5 text-emerald-600">
                        <IndianRupee className="w-5 h-5" />
                        <span className="text-2xl font-extrabold font-mono" data-testid="paid-total">{data.total_paid.toLocaleString("en-IN")}</span>
                      </div>
                    </div>
                  </CardContent>
                </Card>
              </div>

              <Card>
                <CardHeader><CardTitle>All Credits</CardTitle></CardHeader>
                <CardContent className="p-0 overflow-x-auto">
                  <table className="w-full text-sm">
                    <thead className="bg-secondary/60 text-xs uppercase tracking-widest">
                      <tr>
                        <th className="text-left p-3">Referred Order</th>
                        <th className="text-left p-3">Friend</th>
                        <th className="text-right p-3">Amount</th>
                        <th className="text-left p-3">Status</th>
                      </tr>
                    </thead>
                    <tbody>
                      {data.credits.length === 0 && (
                        <tr><td colSpan={4} className="p-8 text-center text-muted-foreground">
                          <div className="flex flex-col items-center gap-2">
                            <Gift className="w-8 h-8 text-muted-foreground/40" />
                            <span>No referrals yet. Share your code with friends to start earning!</span>
                          </div>
                        </td></tr>
                      )}
                      {data.credits.map((c) => (
                        <tr key={c.id} className="border-t border-border" data-testid={`credit-row-${c.id}`}>
                          <td className="p-3">
                            <div className="font-mono font-semibold">{c.referred_order_ref}</div>
                            <div className="text-xs text-muted-foreground">Code: {c.referrer_code}</div>
                          </td>
                          <td className="p-3">
                            <div className="font-medium">{c.referred_customer_name}</div>
                          </td>
                          <td className="p-3 text-right font-mono font-bold text-accent">{formatINR(c.amount)}</td>
                          <td className="p-3">
                            <Badge variant="outline" className={STATUS_STYLES[c.status]}>
                              {c.status === "pending" && <Hourglass className="w-3 h-3 mr-1" />}
                              {c.status === "paid" && <CheckCircle2 className="w-3 h-3 mr-1" />}
                              {c.status === "cancelled" && <XCircle className="w-3 h-3 mr-1" />}
                              {c.status}
                            </Badge>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </CardContent>
              </Card>

              {data.total_pending > 0 && (
                <div className="mt-6 rounded-xl border border-primary/30 bg-primary/5 p-4 text-sm flex items-start gap-3">
                  <MessageCircle className="w-5 h-5 text-primary shrink-0 mt-0.5" />
                  <div>
                    <div className="font-semibold">Awaiting payout?</div>
                    <div className="text-muted-foreground">
                      Payouts happen via UPI within 24 hours of each referred pickup being marked completed. If it&apos;s been longer,
                      ping us on <a href="https://wa.me/918104878405" className="text-primary underline" target="_blank" rel="noreferrer">WhatsApp</a>.
                    </div>
                  </div>
                </div>
              )}
            </>
          )}
        </div>
      </div>
      <Footer />
    </div>
  );
}
